import { useEffect, useRef, useState, useMemo } from "react";
import Map from "ol/Map";
import View from "ol/View";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import HeatmapLayer from "ol/layer/Heatmap";
import GeoJSON from "ol/format/GeoJSON";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import Polygon from "ol/geom/Polygon";
import MultiPolygon from "ol/geom/MultiPolygon";
import { fromLonLat, get as getProjection, toLonLat } from "ol/proj";
import { getWidth } from "ol/extent";
import "ol/ol.css";
import {RiskDataPoint} from "./HistoricalRiskMapCard";

export interface CountryViewCoordinates {
  longitude: number;
  latitude: number;
  zoom: number;
}

interface GeoJSONFeature {
  type: string;
  properties: {
    name: string;
    "ISO3166-1-Alpha-3": string;
    "ISO3166-1-Alpha-2": string;
  };
  geometry: {
    type: string;
    coordinates: number[] | number[][] | number[][][] | number[][][][];
  };
}

interface GeoJSONData {
  type: string;
  features: GeoJSONFeature[];
}

// Calculate bounding box from GeoJSON geometry coordinates
function getBoundingBox(geometry: GeoJSONFeature["geometry"]): [number, number, number, number] {
  let minLon = Infinity, maxLon = -Infinity;
  let minLat = Infinity, maxLat = -Infinity;

  const processCoord = (coord: number[]) => {
    const [lon, lat] = coord;
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  };

  const processCoords = (coords: any) => {
    if (typeof coords[0] === "number") {
      processCoord(coords as number[]);
    } else {
      for (const c of coords) {
        processCoords(c);
      }
    }
  };

  processCoords(geometry.coordinates);
  return [minLon, minLat, maxLon, maxLat];
}

// Calculate zoom level based on bounding box size
function calculateZoom(bbox: [number, number, number, number]): number {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const lonDiff = maxLon - minLon;
  const latDiff = maxLat - minLat;
  const maxDiff = Math.max(lonDiff, latDiff);

  // Approximate zoom based on extent size
  // These thresholds are tuned for a reasonable view
  if (maxDiff > 100) return 2;
  if (maxDiff > 50) return 3;
  if (maxDiff > 25) return 4;
  if (maxDiff > 10) return 5;
  if (maxDiff > 5) return 6;
  if (maxDiff > 2) return 7;
  return 8;
}

// Get country view from GeoJSON data
function getCountryViewFromGeoJSON(
  geoData: GeoJSONData | null,
  iso: string | undefined,
  defaultView: CountryViewCoordinates
): CountryViewCoordinates {
  if (!iso || !geoData) {
    return defaultView;
  }

  const feature = geoData.features.find(
    f => f.properties["ISO3166-1-Alpha-3"] === iso
  );

  if (!feature) {
    return defaultView;
  }

  const bbox = getBoundingBox(feature.geometry);
  const centerLon = (bbox[0] + bbox[2]) / 2;
  const centerLat = (bbox[1] + bbox[3]) / 2;
  const zoom = calculateZoom(bbox);

  return {
    longitude: centerLon,
    latitude: centerLat,
    zoom,
  };
}

// Default view coordinates
const DEFAULT_VIEW: CountryViewCoordinates = { 
  longitude: 60, 
  latitude: 25, 
  zoom: 2.5 
};

interface HeatmapProps {
  width?: string | number;
  height?: string | number;
  initialView?: CountryViewCoordinates | undefined;
  data: RiskDataPoint[];
  iso?: string;
  isLoading?: boolean;
  hasError?: boolean;
}

export default function Heatmap({
  width = "100%",
  height = "100%",
  initialView,
  data,
  iso,
  isLoading = false,
  hasError = false,
}: HeatmapProps) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapObjRef = useRef<Map | null>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [geoData, setGeoData] = useState<GeoJSONData | null>(null);

  // Load GeoJSON data for country view calculation
  useEffect(() => {
    fetch("/countries.geojson")
      .then(res => res.json())
      .then(data => setGeoData(data))
      .catch(err => console.error("Failed to load GeoJSON for view calculation:", err));
  }, []);

  const viewCoordinates = useMemo(() => {
    // If an explicit initial view was provided by the parent (e.g., MapCard),
    // prefer it and skip GeoJSON-based calculation.
    if (
      initialView &&
      typeof initialView.longitude === "number" &&
      typeof initialView.latitude === "number"
    ) {
      return initialView;
    }

    // Otherwise compute view from GeoJSON/ISO or fall back to DEFAULT_VIEW
    return getCountryViewFromGeoJSON(geoData, iso, DEFAULT_VIEW);
  }, [geoData, iso, initialView]);

  useEffect(() => {
    if (!mapRef.current) return;

    // 1) Load country polygons (GeoJSON) into a VectorSource / VectorLayer.
    const projection = getProjection("EPSG:3857");
    const worldExtent = projection?.getExtent();
    const worldWidth = worldExtent ? getWidth(worldExtent) : null;
    const latLimitNorth = fromLonLat([0, 75])[1];
    const latLimitSouth = fromLonLat([0, -65])[1];
    const horizontalPadding = worldWidth ? worldWidth * 3 : 120000000;
    const constrainedExtent = worldExtent
      ? [
          worldExtent[0] - horizontalPadding,
          latLimitSouth,
          worldExtent[2] + horizontalPadding,
          latLimitNorth,
        ]
      : undefined;

    const countriesSource = new VectorSource({
      url: "/countries.geojson", // ensure this file is GeoJSON FeatureCollection of polygons
      format: new GeoJSON(),
      wrapX: true,
    });

    const countriesLayer = new VectorLayer({
      source: countriesSource,
      // invisible; we draw a separate styled layer or keep it visible via CSS
      visible: true,
      opacity: 1,
      zIndex: 1,
    });

    // 2) Convert your points to an OL VectorSource
    const features: Feature[] = data.map((p) => {
      const f = new Feature({
        geometry: new Point(fromLonLat([p.longitude, p.latitude])),
        weight: p.weight ?? 0.5, // default weight is 0.5
        // name: p.name,
        // id: p.id,
      });
      return f;
    });

    const pointsSource = new VectorSource({
      features,
      wrapX: true,
    });

    // 3) Heatmap layer (ol/layer/Heatmap)
    const heatLayer = new HeatmapLayer({
      source: pointsSource,
      blur: 25, // tune: higher -> more blur
      radius: 20, // in pixels
      weight: (feature: Feature) => {
        return (feature.get("weight") as number) ?? 1;
      },
      opacity: 0.95,
      zIndex: 5,
    });

    // 4) Optional: a visible land layer to give the dark silhouette
    const landVisualLayer = new VectorLayer({
      source: countriesSource,
      style: (feature) => {
        // simple style: dark fill, no stroke
        return [
          // We set fill via style objects, but to keep code simple we skip explicit import of Style/Fill here.
        ] as any;
      },
      zIndex: 2,
      opacity: 1,
    });

    // 5) Create the map
    const map = new Map({
      target: mapRef.current!,
      layers: [countriesLayer, heatLayer],
      view: new View({
        center: fromLonLat([viewCoordinates.longitude, viewCoordinates.latitude]),
        zoom: viewCoordinates.zoom,
        minZoom: 1.5,
        maxZoom: 6,
        constrainOnlyCenter: true,
        enableRotation: false,
        multiWorld: true,
        extent: constrainedExtent,
      }),
      controls: [],
    });

    mapObjRef.current = map;

    const computeWrapOffsets = () => {
      if (!map || !worldExtent || !worldWidth || !isFinite(worldWidth)) {
        return [0];
      }
      const size = map.getSize();
      if (!size) return [0];
      const viewExtent = map.getView().calculateExtent(size);
      const baseX = worldExtent[0];
      const start = Math.floor((viewExtent[0] - baseX) / worldWidth);
      const end = Math.floor((viewExtent[2] - baseX) / worldWidth);
      const offsets: number[] = [];
      for (let i = start; i <= end; i += 1) {
        offsets.push(i);
      }
      return offsets.length ? offsets : [0];
    };

    const drawMask = () => {
      const maskCanvas = maskCanvasRef.current;
      if (!maskCanvas || !map) return;
      const ctx = maskCanvas.getContext("2d");
      if (!ctx) return;

      const size = map.getSize();
      if (!size) return;

      const [width, height] = size;
      if (maskCanvas.width !== width || maskCanvas.height !== height) {
        maskCanvas.width = width;
        maskCanvas.height = height;
      }

      ctx.clearRect(0, 0, width, height);

      const features = countriesSource.getFeatures();
      if (!features.length) {
        return;
      }

      ctx.fillStyle = "#0b1220";
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = "destination-out";

      const wrapOffsets = computeWrapOffsets();
      const drawPolygon = (coordinates: number[][][]) => {
        wrapOffsets.forEach((offset) => {
          ctx.beginPath();
          coordinates.forEach((ring) => {
            ring.forEach((coord, idx) => {
              const shiftedX = worldWidth ? coord[0] + offset * worldWidth : coord[0];
              const pixel = map.getPixelFromCoordinate([shiftedX, coord[1]]);
              if (!pixel) return;
              const [x, y] = pixel;
              if (idx === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            });
            ctx.closePath();
          });
          ctx.fill("evenodd");
        });
      };

      features.forEach((feat) => {
        const geom = feat.getGeometry();
        if (!geom) return;
        const type = geom.getType();
        if (type === "Polygon") {
          drawPolygon((geom as Polygon).getCoordinates());
        } else if (type === "MultiPolygon") {
          (geom as MultiPolygon).getCoordinates().forEach(drawPolygon);
        }
      });

      ctx.globalCompositeOperation = "source-over";
    };

    // 6) CLIPPING: clip heat layer to countries polygons
    // We'll use precompose/postcompose on the heat layer to call ctx.clip() before the heat is drawn,
    // and ctx.restore() after so other layers are unaffected.
    const precompose = (event: any) => {
      const ctx = event.context;
      if (!ctx || !map) return;

      // event.context may be a WebGLRenderingContext when OL uses WebGL renderer.
      // WebGL contexts don't have canvas 2D methods like save/restore/clip.
      // Guard: only proceed if we have a 2D canvas context with `save`.
      if (typeof (ctx as any).save !== "function") {
        // Not a 2D canvas context (likely WebGL). Skip canvas clipping here.
        return;
      }

      // Save context
      (ctx as CanvasRenderingContext2D).save();

      // Build a path for all polygon geometries from countriesSource
      // We transform each world coordinate to pixel coords with map.getPixelFromCoordinate
      const features = countriesSource.getFeatures();
      const wrapOffsets = computeWrapOffsets();

      ctx.beginPath();

      for (const feat of features) {
        const geom = feat.getGeometry();
        if (!geom) continue;

        const type = geom.getType();

        // Handle MultiPolygon and Polygon shapes
        // The coordinate arrays are nested differently by type.
        if (type === "MultiPolygon") {
          // MultiPolygon: array of polygons; polygon = array of rings; ring = array of coords
          const multi = (geom as any).getCoordinates();
          for (const polygon of multi) {
            for (const ring of polygon) {
              wrapOffsets.forEach((offset) => {
                ring.forEach((coord: number[], idx: number) => {
                  const shiftedX = worldWidth ? coord[0] + offset * worldWidth : coord[0];
                  const pixel = map.getPixelFromCoordinate([shiftedX, coord[1]]);
                  if (!pixel) return;
                  const [x, y] = pixel;
                  if (idx === 0) ctx.moveTo(x, y);
                  else ctx.lineTo(x, y);
                });
                // close current ring path
                ctx.closePath();
              });
            }
          }
        } else if (type === "Polygon") {
          // Polygon: array of rings
          const poly = (geom as any).getCoordinates();
          for (const ring of poly) {
            wrapOffsets.forEach((offset) => {
              ring.forEach((coord: number[], idx: number) => {
                const shiftedX = worldWidth ? coord[0] + offset * worldWidth : coord[0];
                const pixel = map.getPixelFromCoordinate([shiftedX, coord[1]]);
                if (!pixel) return;
                const [x, y] = pixel;
                if (idx === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
              });
              ctx.closePath();
            });
          }
        } else {
          // skip other types
        }
      }

      // Now clip to the union path we created
      ctx.clip();
      // After this, anything drawn will be clipped to land polygons.
    };

    const postcompose = (event: any) => {
      const ctx = event.context;
      if (!ctx) return;
      // Only restore if this is a 2D canvas context
      if (typeof (ctx as any).restore === "function") {
        try {
          (ctx as CanvasRenderingContext2D).restore();
        } catch (err) {
          // ignore restore errors
        }
      }
    };

    // Attach listeners
    (heatLayer as any).on("precompose", precompose);
    (heatLayer as any).on("postcompose", postcompose);

    // Re-render on moveend so the clip path updates on pan/zoom
    const onMoveEnd = () => {
      map.render();
      drawMask();
    };
    map.on("moveend", onMoveEnd);

    // Log current center (lon/lat) and zoom when the user pans/zooms.
    // To disable this logging, comment out the single line below that attaches the listener.
    const logCenterZoom = () => {
      try {
        const view = map.getView();
        const center = view.getCenter();
        const zoom = view.getZoom();
        if (center) {
          const [lon, lat] = toLonLat(center);
          // eslint-disable-next-line no-console
          console.log(`Map center: ${lon.toFixed(6)}, ${lat.toFixed(6)} | zoom: ${typeof zoom === 'number' ? zoom.toFixed(2) : zoom}`);
        } else {
          // eslint-disable-next-line no-console
          console.log(`Map zoom: ${typeof zoom === 'number' ? zoom.toFixed(2) : zoom}`);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to read map center/zoom', err);
      }
    };

    // Attach logging listener (comment this line out to disable)
    // map.on("moveend", logCenterZoom);

    const onPostRender = () => {
      drawMask();
    };
    map.on("postrender", onPostRender);

    const onFeaturesLoaded = () => {
      drawMask();
    };
    countriesSource.on("featuresloadend", onFeaturesLoaded);

    drawMask();

    // Initial render
    map.render();

    // cleanup on unmount
    return () => {
      (heatLayer as any).un("precompose", precompose);
      (heatLayer as any).un("postcompose", postcompose);
      map.un("moveend", onMoveEnd);
      map.un("moveend", logCenterZoom);
      map.un("postrender", onPostRender);
      countriesSource.un("featuresloadend", onFeaturesLoaded);
      map.setTarget(undefined);
      mapObjRef.current = null;
    };
  }, [viewCoordinates, data]);

  return (
    <div
      style={{
        width,
        height,
        borderRadius: 8,
        overflow: "hidden",
        background: "#0b1220", // page background color to match UI
        border: "1px solid rgba(255,255,255,0.06)",
        position: "relative",
      }}
    >
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
      <canvas
        ref={maskCanvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 10,
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 14,
          bottom: 10,
          color: "#ffffffff",
          fontSize: 12,
          zIndex: 20,
        }}
      >
        {isLoading
          ? "Loading map data..."
          : hasError
          ? "Failed to load map data"
          : data.length > 0
          ? "Five years of security risk data"
          : "No data available"}
      </div>
    </div>
  );
}
